import React, { useState, useCallback, useEffect, useRef } from 'react';
import Confetti from 'react-dom-confetti';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Typography, Tooltip } from '@mui/material';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import { QuestionnaireBlock } from 'src/features/questionnaire_template/components/QuestionnaireBlock';
import {
	IQuestionnaireTemplateStateData,
	IQuestionnaireTemplateValidation,
} from 'src/features/questionnaire_template/domain/types';
import {
	questionnaireTemplateService,
	transformAPIResponseToStateStructure,
	transformStateStructureToAPIRequest,
} from 'src/features/questionnaire_template/service/questionnaireTemplateService';
import {
	clearQuestionnaireUnsavedData,
	setQuestionnaireUnsavedData,
} from 'src/features/questionnaire_template/store';
import {
	createQuestionnaireTemplate,
	getQuestionnaireTemplate,
	QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
} from 'src/features/questionnaire_template/store/questionnaireTemplateThunks';
import { AppDispatch, RootState } from 'src/store';
import { deepOmit } from 'src/utils/formatter';

export const QuestionnaireTemplateBuilderPage: React.FC = () => {
	const { siteSlug, questionnaireTemplateId } = useParams<{
		siteSlug: string;
		questionnaireTemplateId: string;
	}>();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const location = useLocation();

	const questionnaireUnsavedData = useSelector(
		(state: RootState) => state.questionnaire_template.unsavedData,
	);
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[QUESTIONNAIRE_TEMPLATE_LOADING_KEY],
	);

	const generatedIndex = String(location.state?.nextGeneratedIndex || '');
	const initialQuestionnaireData = {
		name: '',
		index: generatedIndex,
		published: false,
		sections: {},
	};
	const getDataForCreateMode = () => {
		if (!questionnaireUnsavedData) return initialQuestionnaireData;

		if (
			!generatedIndex ||
			questionnaireUnsavedData.index === generatedIndex
		) {
			return questionnaireUnsavedData;
		}

		return { ...questionnaireUnsavedData, index: generatedIndex };
	};

	const [showConfetti, setShowConfetti] = useState(false);
	const [mode, setMode] = useState<'edit' | 'create'>(
		questionnaireTemplateId ? 'edit' : 'create',
	);
	const [
		initialQuestionnaireDataonEditMode,
		setinitialQuestionnaireDataOnEditMode,
	] = useState<IQuestionnaireTemplateStateData | undefined>(
		location.state?.initialEditModeData,
	);

	const [questionnaireData, setQuestionnaireData] = useState<
		IQuestionnaireTemplateStateData | undefined
	>(
		mode === 'edit'
			? location.state?.initialEditModeData
			: getDataForCreateMode(),
	);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

	const [questionnaireValidation, setQuestionnaireValidation] =
		useState<IQuestionnaireTemplateValidation>({});
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { validateRequiredData } = questionnaireTemplateService({
		questionnaireData,
		setQuestionnaireValidation,
	});

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{
			label: 'Questionnaire Templates',
			href: `/site/${siteSlug}/questionnaire-templates`,
		},
		{
			label:
				mode === 'create'
					? 'Create Questionnaire Template'
					: 'Edit Questionnaire Template',
		},
	];

	const handleQuestionnaireChange = useCallback(
		(data: IQuestionnaireTemplateStateData) => {
			setQuestionnaireData(data);
		},
		[],
	);

	const handleQuestionnaireDelete = useCallback(() => {
		if (questionnaireUnsavedData && !questionnaireTemplateId) {
			dispatch(clearQuestionnaireUnsavedData());
			setQuestionnaireData(initialQuestionnaireData);
			setHasUnsavedChanges(false);
		}
	}, [dispatch, clearQuestionnaireUnsavedData]);

	const handleSave = () => {
		if (!hasUnsavedChanges) {
			snackbar('No changes detected to submit', 'info', 'center');
			return;
		}

		const isValid = validateRequiredData();
		if (isValid && questionnaireData) {
			const requestBody =
				transformStateStructureToAPIRequest(questionnaireData);

			dispatch(
				createQuestionnaireTemplate({
					requestBody,
					siteSlug: siteSlug!,
					onSuccess: (response) => {
						triggerConfetti();
						const stateData =
							transformAPIResponseToStateStructure(response);

						if (mode === 'create') {
							dispatch(clearQuestionnaireUnsavedData());
							snackbar(
								'Questionnaire template created successfully',
								'success',
								'center',
							);
							navigate(
								`/site/${siteSlug}/questionnaire-template-builder/${response.id}`,
								{
									state: {
										initialEditModeData: stateData,
										showConfetti: true,
									},
								},
							);
						} else if (mode === 'edit') {
							snackbar(
								'Questionnaire template updated successfully',
								'success',
								'center',
							);
							clearLocationStateOnEditMode('initialEditModeData');
							setinitialQuestionnaireDataOnEditMode(stateData);
							setQuestionnaireData(stateData);
							setQuestionnaireValidation({});
							setHasUnsavedChanges(false);
						}
					},
				}),
			);
		} else if (!isValid) {
			snackbar(
				'Form is invalid. Please update before saving',
				'warning',
				'center',
			);
		}
	};

	const triggerConfetti = () => {
		setShowConfetti(true);
		setTimeout(() => setShowConfetti(false), 3000);
	};

	const clearLocationStateOnEditMode = (key: string) => {
		if (!location.state || !(key in location.state)) return;

		const newState = { ...location.state };
		delete newState[key];

		window.history.replaceState(
			{ ...window.history.state, usr: newState },
			'',
		);
	};

	useEffect(() => {
		if (questionnaireTemplateId) {
			if (mode === 'create') setMode('edit');
			if (location.state?.initialEditModeData) return;

			dispatch(
				getQuestionnaireTemplate({
					questionnaireTemplateId: Number(questionnaireTemplateId),
					siteSlug: siteSlug!,
					onSuccess: (response) => {
						const stateData =
							transformAPIResponseToStateStructure(response);

						setinitialQuestionnaireDataOnEditMode(stateData);
						setQuestionnaireData(stateData);
					},
				}),
			);
		} else {
			if (mode === 'edit') setMode('create');
		}
	}, [questionnaireTemplateId]);

	useDeepCompareEffect(() => {
		if (!questionnaireData) return;

		const cleanedData = deepOmit(questionnaireData, ['is_updated']);
		const base =
			mode === 'edit'
				? initialQuestionnaireDataonEditMode
				: initialQuestionnaireData;

		const hasChanges = JSON.stringify(base) !== JSON.stringify(cleanedData);

		setHasUnsavedChanges(hasChanges);
	}, [
		questionnaireData,
		initialQuestionnaireDataonEditMode,
		initialQuestionnaireData,
		mode,
	]);

	useDeepCompareEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(() => {
			if (questionnaireData && mode === 'create') {
				dispatch(setQuestionnaireUnsavedData(questionnaireData));
			}
		}, 350);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [questionnaireData, dispatch, setQuestionnaireUnsavedData]);

	useEffect(() => {
		if (location.state?.showConfetti) {
			triggerConfetti();
			clearLocationStateOnEditMode('showConfetti');
		}
	}, [location.state?.showConfetti]);

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box
				sx={{
					backgroundColor: 'white',
					marginTop: '20px',
					padding: 3,
					borderRadius: '4px',
					position: 'relative',
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '20px',
						lineHeight: '100%',
						letterSpacing: '0.15px',
						mb: 2,
						textAlign: 'left',
					}}
				>
					Questionnaire Template
				</Typography>

				<ContainerLoadingIndicator
					loadingKey={QUESTIONNAIRE_TEMPLATE_LOADING_KEY}
				/>

				{hasUnsavedChanges && (
					<Box
						sx={{
							position: 'sticky',
							top: 0,
							backgroundColor: 'rgba(255, 138, 0, 0.2)',
							color: '#E53326',
							padding: '6px 12px',
							fontSize: '13px',
							fontWeight: 500,
							borderBottom: '1px solid rgb(250, 187, 93)',
							marginBottom: '5px',
							zIndex: 10,
							backdropFilter: 'blur(4px)',
						}}
					>
						<WarningAmberIcon sx={{ fontSize: 18, mr: 1 }} />
						<span>
							Unsaved changes detected
							{mode === 'edit' && (
								<>
									{' '}
									–{' '}
									<span
										style={{
											fontSize: '12px',
										}}
									>
										changes will be lost unless saved.
									</span>
								</>
							)}
						</span>
					</Box>
				)}

				{questionnaireData && (
					<>
						<QuestionnaireBlock
							mode={mode}
							defaultData={questionnaireData}
							onChange={handleQuestionnaireChange}
							validation={questionnaireValidation}
							onDelete={handleQuestionnaireDelete}
						/>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								mt: 2,
							}}
						>
							<Tooltip
								title={
									hasUnsavedChanges
										? 'Hanging by a thread - SAVE ME !'
										: ''
								}
								placement='top'
								arrow
							>
								<PrimaryButton
									isLoading={isLoading}
									onClick={() => {
										handleSave();
									}}
									fullWidth={false}
									sx={{ backgroundColor: '#00000099' }}
								>
									Save Template
								</PrimaryButton>
							</Tooltip>
						</Box>
					</>
				)}

				<Box
					sx={{
						position: 'absolute',
						bottom: 10,
						left: '50%',
						transform: 'translateX(-50%)',
						height: 0,
						pointerEvents: 'none',
					}}
				>
					<Confetti
						active={showConfetti}
						config={{
							angle: 90,
							spread: 120,
							startVelocity: 35,
							elementCount: 100,
							dragFriction: 0.1,
							duration: 3000,
							stagger: 3,
							colors: [
								'#ff0a54',
								'#ff477e',
								'#ff7096',
								'#ff85a1',
							],
						}}
					/>
				</Box>
			</Box>
		</PageWithLayout>
	);
};
